import React from "react";
import {
  Modal,
  Descriptions,
  Table,
  Tag,
  Divider,
  Row,
  Col,
  Avatar,
  ConfigProvider,
} from "antd";
import {
  ShoppingOutlined,
  UserOutlined,
  ShopOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "./orderDetails.css";

const OrderDetailModal = ({
  visible,
  onClose,
  orderData,
  getOrderStatusTagProps,
  getPaymentStatusTagProps,
}) => {
  if (!orderData) return null;

  // Items table columns
  const itemColumns = [
    {
      title: "Product",
      dataIndex: ["product", "productName"],
      key: "name",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Avatar
            src={record.product?.productCoverImage}
            shape="square"
            size="large"
          />
          <span>{text}</span>
        </div>
      ),
    },
    { title: "Qty", dataIndex: "quantity", key: "qty", align: "center" },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (val) => `₦${val.toLocaleString()}`,
    },
    {
      title: "Total",
      dataIndex: "totalPrice",
      key: "total",
      align: "right",
      render: (val) => <strong>₦{val.toLocaleString()}</strong>,
    },
  ];

  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#FFA450" } }}>
      <Modal
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              paddingRight: "40px",
            }}
          >
            <span>Order {orderData.orderNumber}</span>
            <Tag
              {...(getOrderStatusTagProps
                ? getOrderStatusTagProps(orderData.status)
                : {})}
              className="status-tag"
            >
              {orderData.status}
            </Tag>
          </div>
        }
        open={visible}
        onCancel={onClose}
        footer={null}
        width={800}
        className="order-detail-modal"
      >
        <div style={{ marginTop: "20px" }}>
          {/* Section 1: Customer & Vendor */}
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <span className="modal-section-title">
                <UserOutlined /> Customer Details
              </span>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Name">
                  {orderData.customer?.name}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {orderData.customer?.email}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  {orderData.customer?.phoneNumber}
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col xs={24} md={12}>
              <span className="modal-section-title">
                <ShopOutlined /> Vendor Details
              </span>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Store">
                  {orderData.vendor?.storeName}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {orderData.vendor?.email}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  {orderData.vendor?.phoneNumber}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>

          <Divider />

          {/* Section 2: Items Table */}
          <span className="modal-section-title">
            <ShoppingOutlined /> Ordered Items
          </span>
          <Table
            dataSource={orderData.items}
            columns={itemColumns}
            pagination={false}
            rowKey="_id"
            size="small"
            bordered
          />

          {/* Section 3: Delivery & Payment Summary */}
          <Row gutter={[24, 24]} style={{ marginTop: "24px" }}>
            <Col xs={24} md={14}>
              <span className="modal-section-title">Shipping Information</span>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Address">
                  {orderData.deliveryAddress}
                </Descriptions.Item>
                <Descriptions.Item label="Order Date">
                  {dayjs(orderData.orderDate).format("DD MMM YYYY, hh:mm A")}
                </Descriptions.Item>
                <Descriptions.Item label="Payment Ref">
                  <Tag icon={<CreditCardOutlined />}>
                    {orderData.paymentReference}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Payment Status">
                  <Tag
                    {...(getPaymentStatusTagProps
                      ? getPaymentStatusTagProps(orderData.paymentStatus)
                      : {})}
                  >
                    {orderData.paymentStatus || "-"}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Col>

            <Col xs={24} md={10}>
              <div className="summary-container">
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>₦{orderData.subtotal.toLocaleString()}</span>
                </div>
                <div className="summary-row">
                  <span>Delivery Fee</span>
                  <span>+ ₦{orderData.deliveryFee.toLocaleString()}</span>
                </div>
                <div className="summary-total">
                  <span>Total Amount</span>
                  <span> ₦{orderData.totalAmount.toLocaleString()}</span>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default OrderDetailModal;
